<template>
    <div class="Dashboard">
        <!--
        Triggered when the user wishes to navigate to another page

        @event navigate
        @property {object} to The Vue router object or other flag used to trigger navigation
        -->
        <RPSideNav
            :status="status"
            :sections="sections"
            :active-section="activeSection"
            class="Dashboard__side-column"
            @navigate="to => $emit('navigate', to)"
        />
        <div class="Dashboard__main-column">
            <!--
            Triggered when the user wishes to navigate to their profile page
            @event nav-to-profile
            -->
            <!--
            Triggered when the user wishes to sign out of their account
            @event sign-out
            -->
            <RPTopNav
                class="Dashboard__header"
                :active-section="activeSection"
                :subsections="subsections"
                :active-subsection="activeSubsection"
                :user-profile="userProfile"
                :status="status"
                @navigate="to => $emit('navigate', to)"
                @nav-to-profile="$emit('nav-to-profile')"
                @sign-out="e => $emit('sign-out', e)"
            >
                <template v-slot:search>
                    <slot name="search"></slot>
                </template>
            </RPTopNav>
            <div class="Dashboard__content-container">
                <!-- @slot Default Slot for the main content of the site-->
                <template v-if="!isLoading">
                    <div class="Dashboard__content">
                        <slot />
                    </div>
                    <div class="Dashboard__footer">
                        Copyright &copy; 2021 ReviewPush.com. All Rights Reserved.
                        <RPLogo class="Dashboard__footer-icon" />
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import RPLogo from '../../../images/logo/rp-logo-white.svg';
import RPTopNav from 'components/Complex/Navs/RPTopNav.vue';
import RPSideNav from 'components/Complex/Navs/RPSideNav.vue';
import isEmpty from 'lodash/isEmpty';
import has from 'lodash/has';
import stat from 'status/index';

const UserProfileValidator = (user) => {
    return has(user, 'first_name')
        && has(user, 'email')
        && has(user, 'profile_image')
        && has(user, 'role_name');
};

const SectionValidator = (s) => {
    return has(s, 'key')
        && has(s, 'label')
        && has(s, 'icon')
        && has(s, 'to')
        && has(s.to, 'name');
};

const SectionsValidator = (sections) => {
    return sections.filter(s => {
        return SectionValidator(s);
    }).length === sections.length;
};

const SubsectionValidator = (sub) => {
    return isEmpty(sub)
        || (has(sub, 'label')
        && has(sub, 'key')
        && has(sub, 'to')
        && has(sub.to, 'name'));

};

const SubsectionsValidator = (subsections) => {
    return subsections.filter(s => {
        return SubsectionValidator(s);
    }).length === subsections.length;
};

export default {
    name: 'RPDashboardLayout',
    components: {
        RPLogo,
        RPTopNav,
        RPSideNav,
    },
    props: {
        /**
        * The primary sections of the RP Site
        * Each section is formatted {name:'', key: '', label: '', icon: '', to: {name: '' }}
        */
        sections: {
            type: Array,
            required: true,
            validator: SectionsValidator,
        },
        /**
        * The current active section the user is on
        * The section must be formatted:
        * {name:'', label: '', icon: '', to: {name: '' }}
        */
        activeSection: {
            type: Object,
            required: true,
            validator: SectionValidator,
        },
        /**
        * The subsections of the section the user is currently on
        * Each subsection is formatted:
        * {name:'', key: '', label: '', to: {name: '' }}
        */
        subsections: {
            type: Array,
            required: true,
            validator: SubsectionsValidator,
        },
        /**
        * The subsection the user is currently on
        * The subsection must be formatted:
        * {name:'', key: '', label: '', to: {name: '' }}
        */
        activeSubsection: {
            type: Object,
            default: () => ({}),
            validator: SubsectionValidator,
        },
        /**
        * User Profile information used for the User Dropdown
        * Must include:
        * {first_name: '', email: '', profile_image: '', role_name: "" }
        */
        userProfile: {
            type: Object,
            default: () => ({
                first_name: "",
                email: "",
                profile_image: false,
                role_name: "",
            }),
            validator: UserProfileValidator,
        },
        /**
        * Component Status (see status/index.js for list of statuses)
        */
        status: {
            type: String,
            default: stat.STANDBY,
        },
    },
    computed: {
        isLoading(){
            return this.status === stat.LOADING;
        }
    }
};
</script>
<style lang="scss">
   .Dashboard {
       width: inherit;
       height: 100%;
       overflow: hidden;
       display: flex;

       &__side-column {
           flex-shrink: 0;
           height: 100%;
           @media(max-width: $max-sm-width){
                display: none;
           }
       }
       &__header {
           flex-shrink: 0;
       }
       &__main-column {
           flex-grow: 1;
           height: 100%;
           display: flex;
           flex-direction: column;
       }
       &__content-container {
           padding: 1.5rem 1.5rem 0 1.5rem;
           flex-grow: 1;
           overflow-y: auto;
           background: $grey-50;
           display: flex;
           flex-direction: column;
       }
        &__content {
           flex-grow: 1;
       }
       &__footer {
           display: flex;
           flex-direction: row-reverse;
           align-items: center;
           margin-top: 6rem;
           margin-bottom: 1rem;
           color: $grey-300;
           font-weight: 400;
           font-size: 0.85rem;
           line-height: 1rem;
           &-icon {
               height: 1rem;
               margin-right: 0.25rem;
           }
       }
   }
</style>

<docs>
    Primary Dashboard Layout for RP.  It contains the Top Nav and Side Nav as well as the User Profiles Dropdown.

    ```[import](./examples/RPDashboardLayoutExample.vue)
    ```
</docs>
